import type {
  FilterConditionPluginSpecification,
  FilterDetailedText,
} from '@bindoc-projects/filter-condition-domain-lib';
import {
  FilterConditionPluginImpl
} from '@bindoc-projects/filter-condition-domain-lib';

import type {
  FilterConditionDto,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdCondition
} from '@bindoc-projects/condition-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';

export const FILTER_RESOURCE_OPERATING_NUMBER = 'operating-number';

export const OPERATING_NUMBER_FILTER_CONDITION_ID =
  'fullyInpatientCaseOperatingNumberValue';
export const FilterConditionOperatingNumberFilterPluginType: FilterConditionPluginSpecification<
  {
    operatingNumberId: string;
    value: string;
    comparison: FilterConditionOperatingNumberFilterPluginComparison;
  },
  FilterConditionOperatingNumberFilterDto,
  BdOperatingNumberFilterCondition
> = {
  type: 'operatingNumberFilter',
  category: 'case',
  displayName: { de: 'Kennzahl-Filter' },
  sortingIndex: 9,
  occurrence: 'multiple',
  fromObject(
    obj: FilterConditionOperatingNumberFilterDto,
  ): FilterConditionOperatingNumberFilterPlugin {
    return new FilterConditionOperatingNumberFilterPlugin(
      obj.operatingNumberId,
      obj.value != null ? String(obj.value) : obj.value,
      obj.comparison,
    );
  },
  fromCondition(
    jsonObj: BdOperatingNumberFilterCondition,
  ): FilterConditionOperatingNumberFilterPlugin {
    return new FilterConditionOperatingNumberFilterPlugin(
      jsonObj.operatingNumberId,
      jsonObj.value != null ? String(jsonObj.value) : jsonObj.value,
      jsonObj.comparison,
    );
  },
  newInstance(): FilterConditionOperatingNumberFilterPlugin {
    return new FilterConditionOperatingNumberFilterPlugin(
      'counts',
      '0',
      'EQUAL',
    );
  },
};

const COMPARISON_DISPLAY_NAMES = {
  LESS: 'kleiner als',
  LESS_OR_EQUAL: 'kleiner oder gleich',
  EQUAL: 'gleich',
  GREATER_OR_EQUAL: 'größer oder gleich',
  GREATER: 'größer als',
} as const;

export type FilterConditionOperatingNumberFilterPluginComparison =
  keyof typeof COMPARISON_DISPLAY_NAMES;

export const OPERATING_NUMBER_FILTER_COMPARISON_DISPLAY_NAMES: {
  [comparison in FilterConditionOperatingNumberFilterPluginComparison]: string;
} = COMPARISON_DISPLAY_NAMES;

export type OperatingNumberFilterComparisonDisplayName =
  (typeof COMPARISON_DISPLAY_NAMES)[keyof typeof COMPARISON_DISPLAY_NAMES];

export class FilterConditionOperatingNumberFilterPlugin extends FilterConditionPluginImpl<
  {
    operatingNumberId: string;
    value: string;
    comparison: FilterConditionOperatingNumberFilterPluginComparison;
  },
  FilterConditionOperatingNumberFilterDto
> {
  static override Specification: FilterConditionPluginSpecification<
    {
      operatingNumberId: string;
      value: string;
      comparison: FilterConditionOperatingNumberFilterPluginComparison;
    },
    FilterConditionOperatingNumberFilterDto,
    BdOperatingNumberFilterCondition
  > = FilterConditionOperatingNumberFilterPluginType;

  constructor(
    public operatingNumberId: string,
    public value: string,
    public comparison: FilterConditionOperatingNumberFilterPluginComparison,
  ) {
    super(FilterConditionOperatingNumberFilterPluginType.type);
  }

  clone(): FilterConditionOperatingNumberFilterPlugin {
    return new FilterConditionOperatingNumberFilterPlugin(
      this.operatingNumberId,
      this.value,
      this.comparison,
    );
  }

  override equals(other: FilterConditionOperatingNumberFilterPlugin): boolean {
    return (
      super.equals(other) &&
      BdEquals.equals(this.value, other.value) &&
      BdEquals.equals(this.operatingNumberId, other.operatingNumberId) &&
      BdEquals.equals(this.comparison, other.comparison)
    );
  }

  override valid(): boolean {
    return (
      !!this.operatingNumberId &&
      !!this.value &&
      this.value.match('^-?\\d+[.,]?\\d*$') &&
      !!this.comparison
    );
  }

  override validationToText(): string {
    if (!this.operatingNumberId) {
      return 'Kennzahl auswählen';
    } else if (!this.comparison) {
      return 'Vergleich auswählen';
    } else if (!Number.isInteger(this.value)) {
      return 'Wert auswählen';
    } else {
      return '';
    }
  }

  override toCondition(): BdCondition | null {
    return new BdOperatingNumberFilterCondition(
      this.operatingNumberId,
      this.value,
      this.comparison,
    );
  }

  toJSON(): FilterConditionOperatingNumberFilterDto | null {
    return {
      type: this.type,
      comparison: this.comparison,
      value: this.value.toString(),
      operatingNumberId: this.operatingNumberId,
    };
  }

  toText(): string | null {
    const comparisonText = COMPARISON_DISPLAY_NAMES[this.comparison];
    if (!comparisonText) {
      return null;
    }
    return `Kennzahl ist ${comparisonText} ${this.value}`;
  }

  override getExtraChildResources(): Record<string, string[]> {
    if (!this.operatingNumberId) return {};
    return {
      [FILTER_RESOURCE_OPERATING_NUMBER]: [this.operatingNumberId],
    };
  }

  override toDetailedText(
    resolvedNames?: Map<string, string>,
  ): FilterDetailedText | null {
    const comparisonText = COMPARISON_DISPLAY_NAMES[this.comparison];
    if (!comparisonText) return null;
    const name =
      resolvedNames?.get(this.operatingNumberId) ?? this.operatingNumberId;
    return {
      title: `Kennzahl ${name} ist ${comparisonText} ${this.value}`,
      items: [],
    };
  }

  toValue(): {
    operatingNumberId: string;
    value: string;
    comparison: FilterConditionOperatingNumberFilterPluginComparison;
  } {
    return {
      comparison: this.comparison,
      operatingNumberId: this.operatingNumberId,
      value: this.value,
    };
  }
}

export class BdOperatingNumberFilterCondition extends BdCondition {
  constructor(
    public operatingNumberId: string,
    public value: string,
    public comparison:
      | 'LESS'
      | 'LESS_OR_EQUAL'
      | 'EQUAL'
      | 'GREATER_OR_EQUAL'
      | 'GREATER',
  ) {
    super(OPERATING_NUMBER_FILTER_CONDITION_ID);
  }

  toJSON(): FilterConditionDto {
    return {
      [this.id]: {
        operatingNumberId: this.operatingNumberId,
        value: this.value.replace(',', '.'),
        comparison: this.comparison,
      },
    };
  }
}

interface FilterConditionOperatingNumberFilterDto {
  type: string;
  operatingNumberId: string;
  value: string;
  comparison: FilterConditionOperatingNumberFilterPluginComparison;
}
